// Офлайн-очередь исходящих. SPEC client-transport §4.5 (инв.1) / §4.6 (инв.2) / §4.9.
//
// Инвариант 1: каждое исходящее несёт clientMsgId; payload без него → отказ ДО сети (VALIDATION).
// Инвариант 2: офлайн-сообщение не теряется — пишем 'queued' в OutboxStore ДО сети, дослыл при
// reconnect/online строго FIFO по enqueuedAt, по одному POST за раз (порядок per-chat). Идемпотентность
// (тот же clientMsgId) на сервере защищает от дублей при ретрае после потери ответа.
import { TransportError } from './errors.js';
/** Коды, на которых ретрай бессмысленен → статус 'failed' (SPEC §4.6.5). */
const NON_RETRIABLE = new Set([
    'VALIDATION',
    'FORBIDDEN',
    'NOT_FOUND',
    'PAYLOAD_TOO_LARGE',
]);
/**
 * Реализация очереди исходящих. Состояние держится в OutboxStore (durable) + лёгкий in-memory
 * индекс для порядка/защиты от параллельного flush. Один flush за раз (mutex), FIFO по enqueuedAt.
 */
export class OutboxQueue {
    deps;
    flushing = false;
    /** Перезапросить flush после текущего, если в процессе что-то добавили/онлайн вернулся. */
    flushRequested = false;
    constructor(deps) {
        this.deps = deps;
    }
    /** Восстанавливает очередь из store при старте (SPEC §4.1 start: «восстановить очередь»). */
    async restore() {
        const items = await this.deps.store.getAll();
        // 'sent' без подтверждения после рестарта возвращаем в 'queued' для безопасного дослыла
        // (идемпотентность по clientMsgId защитит от дубля, SPEC §4.9 «потеря сети в момент отправки»).
        for (const it of items) {
            if (it.status === 'sent') {
                const back = { ...it, status: 'queued' };
                await this.deps.store.put(back);
                this.deps.onChanged(back);
            }
        }
    }
    async enqueue(chatId, dto) {
        // Инвариант 1: clientMsgId обязателен и непуст — иначе отказ ДО сети.
        if (typeof dto.clientMsgId !== 'string' || dto.clientMsgId.length === 0) {
            throw new TransportError('VALIDATION', 'outgoing message requires non-empty clientMsgId');
        }
        const item = {
            clientMsgId: dto.clientMsgId,
            chatId,
            dto,
            status: 'queued',
            attempts: 0,
            enqueuedAt: this.deps.now(),
        };
        // Инвариант 2: пишем 'queued' в durable-store ДО сети (переживёт мгновенное закрытие вкладки).
        await this.deps.store.put(item);
        this.deps.onChanged(item);
        // Пытаемся отправить немедленно (если готовы). Ошибки flush не пробрасываем из enqueue —
        // сообщение уже в очереди и не потеряется.
        void this.flush();
        return item;
    }
    async list() {
        const items = await this.deps.store.getAll();
        return items.sort((a, b) => a.enqueuedAt - b.enqueuedAt);
    }
    async retry(clientMsgId) {
        const items = await this.deps.store.getAll();
        const it = items.find((i) => i.clientMsgId === clientMsgId);
        if (!it)
            return;
        if (it.status === 'failed' || it.status === 'queued') {
            const requeued = { ...it, status: 'queued', lastError: undefined };
            await this.deps.store.put(requeued);
            this.deps.onChanged(requeued);
            void this.flush();
        }
    }
    async remove(clientMsgId) {
        await this.deps.store.delete(clientMsgId);
    }
    /**
     * Сводит исходящее с серверным `ack` (WS) по clientMsgId: 'sent'/'queued' → 'delivered', затем
     * удаляет из durable-очереди (сообщение зафиксировано сервером). SPEC §4.3.7 / §4.6.2.
     */
    async onAck(clientMsgId) {
        const items = await this.deps.store.getAll();
        const it = items.find((i) => i.clientMsgId === clientMsgId);
        if (!it)
            return;
        const delivered = { ...it, status: 'delivered' };
        this.deps.onChanged(delivered);
        await this.deps.store.delete(clientMsgId);
    }
    /**
     * Сливает очередь: FIFO по enqueuedAt, по ОДНОМУ POST за раз (порядок per-chat). Mutex — один
     * flush одновременно; повторные вызовы во время flush ставят флаг «перезапустить». Вызывается из
     * enqueue, на `online` и на WS-`ready` (reconnect → дослать, SPEC §4.6.4).
     */
    async flush() {
        if (this.flushing) {
            this.flushRequested = true;
            return;
        }
        this.flushing = true;
        try {
            do {
                this.flushRequested = false;
                if (!this.deps.canSend())
                    return;
                const pending = (await this.deps.store.getAll())
                    .filter((i) => i.status === 'queued')
                    .sort((a, b) => a.enqueuedAt - b.enqueuedAt);
                for (const item of pending) {
                    if (!this.deps.canSend())
                        return;
                    const ok = await this.sendOne(item);
                    if (!ok) {
                        // сеть отвалилась/ретраибельная ошибка — прекращаем проход, не нарушая порядок.
                        if (!this.deps.canSend())
                            return;
                    }
                }
            } while (this.flushRequested && this.deps.canSend());
        }
        finally {
            this.flushing = false;
        }
    }
    /** Отправляет один элемент; обновляет статус. Возвращает true при успехе REST. */
    async sendOne(item) {
        try {
            const message = await this.deps.sender.sendMessage(item.chatId, item.dto);
            // Успех REST = сервер ВЕРНУЛ зафиксированный Message — это и есть подтверждение доставки
            // на сервер: сразу 'delivered' + durable-удаление. Ждать WS-`ack` нельзя: при лежащем WS
            // (client-diag 2026-07-05) ack не придёт никогда, и запись зависала бы 'sent' в store,
            // передосылаясь на каждый рестарт (идемпотентность спасала от дублей, но не от мусора).
            // WS-`ack` по этому clientMsgId теперь штатно no-op (onAck: записи уже нет).
            const delivered = { ...item, status: 'delivered', attempts: item.attempts + 1 };
            await this.deps.store.delete(item.clientMsgId);
            this.deps.onChanged(delivered);
            this.deps.onSent?.(delivered, message);
            return true;
        }
        catch (err) {
            return await this.onSendError(item, err);
        }
    }
    /** Классифицирует ошибку отправки: 'failed' (не ретраить) или остаёмся 'queued' (ретрай позже). */
    async onSendError(item, err) {
        const code = err instanceof TransportError ? err.code : 'INTERNAL';
        const message = err instanceof Error ? err.message : String(err);
        const attempts = item.attempts + 1;
        if (NON_RETRIABLE.has(code)) {
            const failed = { ...item, status: 'failed', attempts, lastError: { code, message } };
            await this.deps.store.put(failed);
            this.deps.onChanged(failed);
            return false;
        }
        // UNAUTHENTICATED → остаёмся queued, ждём релогин (фабрика остановит flush через canSend).
        // RATE_LIMITED / INTERNAL / сеть → остаёмся queued, ретрай по online/ready (backoff на WS-уровне).
        const requeued = { ...item, status: 'queued', attempts, lastError: { code, message } };
        await this.deps.store.put(requeued);
        this.deps.onChanged(requeued);
        return false;
    }
}
//# sourceMappingURL=outbox.js.map